import { useMediaQuery } from "@mui/material";
import { isViewRNativeAuthWebView } from "../utils/webViewUtils";

/**
 * 모바일 라우팅 판정 기준.
 *
 * 판정은 창 폭이 아니라 기기 특성으로 합니다. 데스크톱 브라우저는 창을 아무리 좁혀도
 * 데스크톱 라우트를 유지하고, 모바일 라우트로 직접 들어와도 데스크톱으로 돌아갑니다.
 * 좁은 데스크톱 창은 라우트 교체가 아니라 반응형 레이아웃으로 대응합니다.
 */

/** 주 입력 수단이 터치인 기기. 트랙패드·마우스가 있는 데스크톱은 화면이 터치여도 false. */
export const COARSE_POINTER_QUERY = "(pointer: coarse) and (hover: none)";

/** 폰 화면으로 볼 기기 화면 짧은 변의 상한. 기존 모바일 레이아웃 breakpoint와 같은 값. */
export const PHONE_SCREEN_MAX_EDGE_PX = 767.95;

const SPATIAL_AGENT_PATTERN = /visionos|vision pro|realityos/;

interface ScreenMetrics {
  width: number;
  height: number;
}

/**
 * 창 크기가 아니라 기기 화면 크기로 판정합니다. `window.screen`은 창을 좁혀도 바뀌지
 * 않으므로 좁힌 데스크톱 창이 폰으로 오인되지 않습니다. 회전에 상관없도록 짧은 변을 봅니다.
 */
export function hasPhoneSizedScreen(screen: ScreenMetrics | undefined): boolean {
  if (!screen) return false;

  const shortestEdge = Math.min(screen.width, screen.height);
  return shortestEdge > 0 && shortestEdge <= PHONE_SCREEN_MAX_EDGE_PX;
}

/** visionOS(AVP)는 폰 레이아웃 대상이 아닙니다. */
export function isSpatialAgent(userAgent: string): boolean {
  return SPATIAL_AGENT_PATTERN.test(userAgent.toLowerCase());
}

export function resolvesToMobileDevice({
  coarsePointer,
  phoneSizedScreen,
  spatialAgent,
  nativeSpatialWebView = false,
}: {
  coarsePointer: boolean;
  phoneSizedScreen: boolean;
  spatialAgent: boolean;
  /**
   * ViewR AVP 앱 안의 WKWebView인지.
   *
   * AVP WebView는 User-Agent를 Mac Safari 문자열로 보내므로(`ViewRWebView.swift`)
   * `isSpatialAgent`가 visionOS를 찾지 못합니다. 시선 입력은 coarse pointer로 보고되고
   * `window.screen`의 짧은 변이 폰 기준을 넘지 못하면 폰으로 오인되어 `/mobile`로
   * 리다이렉트됩니다. 그러면 `LoginPage`가 `variant="mobile"`로 렌더되어
   * Guest 버튼이 사라지고 컬럼이 342로 좁아집니다.
   */
  nativeSpatialWebView?: boolean;
}): boolean {
  if (spatialAgent || nativeSpatialWebView) return false;

  return coarsePointer && phoneSizedScreen;
}

export function useIsMobileDevice(): boolean {
  const coarsePointer = useMediaQuery(COARSE_POINTER_QUERY, { noSsr: true });

  return resolvesToMobileDevice({
    coarsePointer,
    phoneSizedScreen: hasPhoneSizedScreen(
      typeof window === "undefined" ? undefined : window.screen,
    ),
    spatialAgent:
      typeof navigator === "undefined"
        ? false
        : isSpatialAgent(navigator.userAgent),
    nativeSpatialWebView: isViewRNativeAuthWebView(),
  });
}

const DESKTOP_PATH_BY_MOBILE_PATH: Record<string, string> = {
  "/mobile": "/login",
  "/mobile/sign-in": "/login",
};

/**
 * 데스크톱 기기가 모바일 라우트에 있을 때 돌려보낼 경로.
 *
 * 인증이 필요한 경로는 `/home`으로 보내고 실제 인증 판정은 `ProtectedRoute`에 맡깁니다.
 * `/mobile/viewer/:viewrId`는 대응하는 데스크톱 deeplink(`/editor/:projectId`)가 아직
 * 없으므로 `/home`으로 보냅니다.
 */
export function desktopPathForMobilePath(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "") || "/mobile";

  return DESKTOP_PATH_BY_MOBILE_PATH[normalized] ?? "/home";
}
